<template>
  <b-field
    :label="label"
    :horizontal="horizontal"
    :type="type"
    :message="message"
    :custom-class="customClass"
    class="base-tag-input"
  >
    <b-taginput
      v-model="tagsSelected"
      :data="tagsFiltered"
      field="name"
      :placeholder="placeholder || (_allowNew ? 'Add tag' : 'Select tag')"
      :open-on-focus="openOnFocus"
      :allow-new="_allowNew"
      autocomplete
      clear-on-select
      :size="size"
      :disabled="disabled"
      :loading="loading"
      @typing="getTagsFiltered"
      @add="onAdd"
      @remove="onRemove"
      @blur="onBlur"
    >
      <template slot="empty">No Result</template>
    </b-taginput>
  </b-field>
</template>

<script>
import { TagService } from "@/services/tag.service";
import { JsonDenormalizer } from "@/helpers/JsonDenormalizer";
import debounce from "lodash/debounce";

export default {
  props: {
    // Need a better name :grimacing:
    attachMode: {
      type: Boolean,
      default: false,
    },
    modelType: {
      type: String,
    },
    modelId: {
      type: [String, Number],
    },
    tagType: {
      type: String,
    },
    fetchParams: {
      type: Object,
      default: () => {},
    },
    fetchOnInit: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: "Tags",
    },
    placeholder: {
      type: String,
    },
    prepend: {
      type: Array,
    },
    openOnFocus: {
      type: Boolean,
      default: false,
    },
    allowNew: {
      type: Boolean,
    },
    horizontal: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
    },
    type: {
      type: String,
    },
    message: {
      type: String,
    },
    customClass: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    customFormat: {},
  },
  data: () => ({
    tags: [],
    tagsFiltered: [],
    tagsSelected: [],
    // Previously selected tags
    tagsSelectedPrev: [],
    loading: false,
    _allowNew: false,
  }),
  computed: {
    user() {
      return this.$store.state.Auth.user;
    },
  },
  created() {
    if (this.fetchOnInit) {
      this.fetchTags();
    }

    // Default allowNew value is true if in attach mode
    if (typeof this.allowNew === "boolean") {
      this._allowNew = this.allowNew;
    } else {
      this._allowNew = this.attachMode;
    }
  },
  watch: {
    value: {
      handler: function (value) {
        // Clone to avoid unnecessary reactivity?
        this.tagsSelected = JSON.parse(JSON.stringify(value));
        this.tagsSelectedPrev = JSON.parse(JSON.stringify(value));
      },
      immediate: true,
    },
    tagsSelected() {
      if (this.openOnFocus) {
        setTimeout(() => {
          document
            .querySelector(".base-tag-input .dropdown-menu")
            .removeAttribute("style");
          document
            .querySelector(".base-tag-input .dropdown-content")
            .removeAttribute("style");
        }, 200);
      }
    },
  },
  methods: {
    async fetchTags() {
      try {
        let tags = await TagService.all({
          firm_id: this.user.firmId,
          ...this.fetchParams,
        }).then((response) => JsonDenormalizer.denormalize(response));

        if (this.customFormat) {
          tags = this.customFormat(tags);
        }

        // Exclude this tags?
        tags = tags.filter(
          (tag) =>
            !["vendor", "customer", "colleagues"].includes(
              tag.name.toLowerCase()
            )
        );

        if (this.prepend) {
          tags.unshift(...this.prepend);
        }

        this.tags = tags;
        this.resetTagsFiltered();
      } catch (err) {}
    },
    resetTagsFiltered() {
      this.tagsFiltered = this.tags.map((tag) => tag.name);
    },
    getTagsFiltered: debounce(function (filter) {
      this.tagsFiltered = this.tags
        .filter((tag) => {
          return (
            tag.name.toString().toLowerCase().indexOf(filter.toLowerCase()) >= 0
          );
        })
        .map((tag) => tag.name);
    }, 200),
    // Attach/Detach by name only if (allow new )
    async onAdd(tagName) {
      try {
        if (this.attachMode) {
          this.loading = true;

          const tag = this.tags.find((tag) => tag.name == tagName);
          const data = {
            model_type: this.modelType,
            model_id: this.modelId,
          };

          let attached = null;
          if (tag && !this._allowNew) {
            attached = await TagService.attach({
              ...data,
              tag_ids: [tag.id],
            }).then((response) => JsonDenormalizer.denormalize(response));
          } else {
            attached = await TagService.attachByNames({
              ...data,
              tag_type: this.tagType || undefined,
              tag_names: [tagName],
            }).then((response) => JsonDenormalizer.denormalize(response));
          }
          console.log(attached);
          this.$emit("attached", attached);
        }

        // Update previous selected tags
        this.tagsSelectedPrev = this.tagsSelected;

        this.resetTagsFiltered();

        this.$emit("add", tagName);
        this.$emit("update:value", this.tagsSelected);
      } catch (err) {
        // Revert to previous value
        this.tagsSelected = JSON.parse(JSON.stringify(this.tagsSelectedPrev));
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    async onRemove(tagName) {
      try {
        if (this.attachMode) {
          this.loading = true;

          const tag = this.tags.find((tag) => tag.name == tagName);
          const data = {
            model_type: this.modelType,
            model_id: this.modelId,
          };

          if (tag && !this._allowNew) {
            await TagService.detach({
              ...data,
              tag_ids: [tag.id],
            });
          } else {
            await TagService.detachByNames({
              ...data,
              tag_type: this.tagType || undefined,
              tag_names: [tagName],
            });
          }
        }

        // Update previous selected tags
        this.tagsSelectedPrev = this.tagsSelected;

        this.resetTagsFiltered();

        this.$emit("remove", tagName);
        this.$emit("update:value", this.tagsSelected);
      } catch (err) {
        // Revert to previous value
        this.tagsSelected = JSON.parse(JSON.stringify(this.tagsSelectedPrev));
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    onBlur() {
      if (this.openOnFocus) {
        setTimeout(() => {
          document
            .querySelector(".base-tag-input .dropdown-menu")
            .setAttribute("style", "display:none;");
          document
            .querySelector(".base-tag-input .dropdown-content")
            .setAttribute("style", "display:none;");
        }, 200);
      }
    },
  },
};
</script>

<style lang="scss">
// .base-tag-input {
//   .taginput-container.is-focused {
//     .dropdown-menu,
//     .dropdown-content {
//       display: block !important;
//     }
//   }
// }
</style>


// WEBPACK FOOTER //
// src/components/partials/BaseTagInput.vue